// VoiceRecorder — press-and-record UI that ships the resulting webm/opus blob
// to `/api/items/{id}/voice-note`, which transcribes via Whisper and posts a
// comment. Batch 2 · Feb 2026.
//
// Design choices:
//   • MediaRecorder default codec (audio/webm; codecs=opus) — Whisper accepts it.
//   • Mic permission gate is lazy — only prompt when the user hits Record.
//   • Simple state machine: idle → recording → uploading → idle | error.
//   • No auto-restart on error — we surface the message and let the user retry.

import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Mic, Square, Loader2, AlertCircle } from "lucide-react";

export default function VoiceRecorder({ itemId, onTranscribed }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | error
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const chunksRef = useRef([]);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => { stopStream(); if (timerRef.current) clearInterval(timerRef.current); }, []);

  const stopStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
  };

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Microphone not supported in this browser.");
      setState("error"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        upload(blob);
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((n) => n + 1), 1000);
    } catch (e) {
      setError(e.message || "Microphone permission denied.");
      setState("error");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { recRef.current?.stop(); } catch { /* noop */ }
    setState("uploading");
  };

  const upload = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      const { data } = await api.post(`/items/${itemId}/voice-note`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onTranscribed && onTranscribed(data.comment);
      setState("idle");
      setElapsed(0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
      setState("error");
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="flex items-center gap-2" data-testid="voice-recorder">
      {state === "idle" && (
        <button
          type="button"
          onClick={start}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-400/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 text-xs"
          data-testid="voice-record-start"
          title="Record a voice note — Whisper will transcribe it"
        >
          <Mic className="w-3.5 h-3.5" /> Record voice
        </button>
      )}
      {state === "recording" && (
        <>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-rose-500/20 border border-rose-400/60 text-rose-100 text-xs font-mono">
            <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
            REC {mm}:{ss}
          </span>
          <button
            type="button"
            onClick={stop}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 text-slate-200 text-xs"
            data-testid="voice-record-stop"
          >
            <Square className="w-3 h-3" /> Stop &amp; transcribe
          </button>
        </>
      )}
      {state === "uploading" && (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-orange-400/40 bg-orange-500/10 text-orange-200 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing…
        </span>
      )}
      {state === "error" && (
        <>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 text-xs" data-testid="voice-record-error">
            <AlertCircle className="w-3.5 h-3.5" /> {error || "Failed"}
          </span>
          <button type="button" onClick={() => { setState("idle"); setError(""); }} className="text-xs text-slate-400 hover:text-white">Reset</button>
        </>
      )}
    </div>
  );
}
